import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import { useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { Outlet } from "react-router-dom";
import CompanyDetailsForm from "./CompanyDetailsForm";
import PurchaseHistoryStep from "./PurchaseHistoryStep";
import { useSiteConfig } from "../siteConfig";
import EmailVerificationPrompt from "../common/components/userAuth/EmailVerificationPrompt";
import { useMiscStore, useUser } from "../common/stateDefs";
import { PastQuerySearch } from "./InventorySearch/PastQuerySearch";
import { BackendNotifications } from "./BackendNotifications";
import { Sidebar } from "./Sidebar";
import { NewVersionNotice } from "./NewVersionNotice";
import { SuperUserToggle } from "./SuperUser/SuperUserToggle";

const PortalContainer = () => {
  const { authUser } = useFirebaseAuthentication();
  const user = useUser((state) => state.user);
  const setUser = useUser((state) => state.setUser);
  const companyDetailsRequired = useMiscStore(
    (state) => state.companyDetailsRequired,
  );
  const siteConfig = useSiteConfig();

  useEffect(() => {
    if (!authUser) return;
    const q = doc(db, "users", authUser.uid);
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setUser({
            id: authUser.uid,
            email: data.email,
            displayName: data.displayName || authUser.displayName || "",
            company: data.company,
            roles: data.roles || [],
            userType: data.userType,
            companyOnboardingSkipped: data.companyOnboardingSkipped === true,
            purchaseHistory: data.purchaseHistory,
          });
        } else {
          console.warn("No profile doc at users/" + authUser.uid);
        }
      },
      (err) => console.error("profile doc subscription error", err),
    );
    return () => unsubscribe();
  }, [authUser]);

  const email = (user?.email || authUser?.email || "").toLowerCase();
  const isKonoTeam = email.endsWith("@kono.ai");
  const needsCompanyAddress =
    !isKonoTeam &&
    (companyDetailsRequired ||
      (!!user && !user.company && !user.companyOnboardingSkipped));

  if (needsCompanyAddress) {
    return <CompanyDetailsForm />;
  }

  // Optional onboarding step once the company is known: share the shop's
  // purchase history (skippable; offered again under My information). Only
  // when switched on in Sales and UI Configurations.
  const needsPurchaseHistory =
    siteConfig?.signupPurchaseHistoryStep === true &&
    !isKonoTeam &&
    !!user?.company &&
    !user.purchaseHistory?.uploadedAt &&
    !user.purchaseHistory?.skippedAt;

  if (needsPurchaseHistory) {
    return <PurchaseHistoryStep />;
  }

  if (!!authUser && !authUser.emailVerified) {
    return <EmailVerificationPrompt />;
  }

  return (
    <>
      {process.env.REACT_APP_PAST_QUERY_SEARCHES_ENABLED && <PastQuerySearch />}
      <BackendNotifications />
      <Sidebar />
      <NewVersionNotice />
      {/* Kono staff only. Rendered in the shell so the corner control follows
          them across pages, while the tools it unlocks live on the product
          page. The type is re-checked on every API call, so this is what to
          show, not what to trust. */}
      {user?.userType === "kono_super" && <SuperUserToggle />}
      <Outlet />
    </>
  );
};

export default PortalContainer;
